import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { AlertTriangle, X, Image as ImageIcon } from "lucide-react";

/**
 * Owner-only banner that surfaces tenant brand-logo URLs (and any
 * other remote PDF assets) that have silently fallen back to the
 * transparent placeholder during a board-pack / ROI / maturity PDF
 * render in the last 7 days.
 *
 * Empty state: renders nothing. Dismissable for the session.
 */
export function PdfAssetFailuresBanner() {
  const [items, setItems] = useState([]);
  const [dismissed, setDismissed] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/owner/pdf-asset-failures");
      // We only surface URLs that map to a tenant — those are the ones
      // the Owner can actually fix by updating the tenant record.
      const withTenant = (data?.items || []).filter((it) => (it.tenants || []).length > 0);
      setItems(withTenant);
    } catch {
      // Owner-only endpoint; if it 4xx-es we silently no-op (e.g. a
      // future non-Owner viewer accidentally on this page).
      setItems([]);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (dismissed || items.length === 0) return null;

  const totalCount = items.reduce((acc, it) => acc + (it.count || 0), 0);
  const tenantCount = new Set(
    items.flatMap((it) => (it.tenants || []).map((t) => t.tenant_id)),
  ).size;

  return (
    <div
      className="mt-6 rounded-2xl border border-amber-300 bg-amber-50 p-4"
      data-testid="pdf-asset-failures-banner"
      role="status"
    >
      <div className="flex items-start gap-3">
        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-700" aria-hidden />
        <div className="flex-1">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-amber-900">
                {tenantCount} tenant{tenantCount === 1 ? "" : "s"} with a failing brand logo
              </p>
              <p className="mt-0.5 text-xs text-amber-800">
                {totalCount} placeholder fallback{totalCount === 1 ? "" : "s"} in the
                last 7 days. Board packs and other PDFs are still shipping, but the
                logo is missing on them.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setDismissed(true)}
              className="-mr-1 -mt-1 rounded p-1 text-amber-700 hover:bg-amber-100"
              aria-label="Dismiss banner"
              data-testid="pdf-asset-failures-dismiss"
            >
              <X className="h-4 w-4" aria-hidden />
            </button>
          </div>

          <ul className="mt-3 space-y-2" data-testid="pdf-asset-failures-list">
            {items.slice(0, 5).map((it) => (
              <li
                key={it.url}
                className="rounded-md border border-amber-200 bg-white px-3 py-2 text-xs"
                data-testid="pdf-asset-failure-row"
              >
                <div className="flex items-center gap-2 text-amber-900">
                  <ImageIcon className="h-3.5 w-3.5 shrink-0" aria-hidden />
                  <code className="truncate font-mono">{it.url}</code>
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-600">
                  <span>
                    {it.count} miss{it.count === 1 ? "" : "es"}
                  </span>
                  <span>·</span>
                  <span>
                    {(it.tenants || []).map((t) => t.organisation).join(", ")}
                  </span>
                  {it.last_error && (
                    <>
                      <span>·</span>
                      <span className="italic">{it.last_error}</span>
                    </>
                  )}
                </div>
              </li>
            ))}
            {items.length > 5 && (
              <li className="text-xs text-amber-800">
                +{items.length - 5} more — `GET /api/owner/pdf-asset-failures` for the full list.
              </li>
            )}
          </ul>
        </div>
      </div>
    </div>
  );
}
